/**
 * A marca do banco.
 *
 * Sao os simbolos oficiais, baixados uma vez dos proprios sites das
 * instituicoes e servidos daqui — nao de um servidor de terceiro. A diferenca
 * importa: buscar o icone na hora contaria a esse terceiro, a cada vez que a
 * tela abre, quais bancos esta casa usa.
 *
 * Nem toda instituicao entra. Cada imagem foi conferida a olho antes de
 * entrar, e as que vieram erradas ou pequenas demais ficaram de fora e usam a
 * marca desenhada: cor da instituicao e inicial. Um simbolo errado e pior que
 * nenhum, porque quem bate o olho numa lista de saldos usa a cor para achar a
 * linha, e nao le o nome.
 *
 * O logotipo da Pluggy, que seria o caminho obvio, nao serve: o conector
 * guardado nas conexoes desta casa e o mesmo para bancos diferentes, e a
 * imagem dele entregaria o simbolo do Itau sobre o saldo do Nubank.
 */

interface Marca {
  /** Arquivo em `public/bancos`, quando ha simbolo oficial conferido. */
  logo?: string;
  /** O que aparece no quadrado desenhado, quando nao ha. Duas letras. */
  sigla: string;
  fundo: string;
  texto: string;
}

/**
 * Reconhecidas pelo nome, e nao pelo codigo COMPE: conta criada na mao pode
 * nao ter codigo, e o nome sempre existe. A busca e por pedaco, porque o
 * banco se apresenta de varios jeitos ("Itau", "Itau Unibanco", "ITAU PF").
 */
const MARCAS: Array<{ procurar: string; marca: Marca }> = [
  // Nao e banco, e onde o escritorio recebe — mas aparece na mesma lista de
  // saldos, e ali o que importa e distinguir de relance de onde sai o dinheiro.
  { procurar: 'asaas', marca: { logo: 'asaas', sigla: 'as', fundo: '#1f5af6', texto: '#ffffff' } },
  {
    procurar: 'nubank',
    marca: { logo: 'nubank', sigla: 'nu', fundo: '#820ad1', texto: '#ffffff' },
  },
  {
    procurar: 'nu pagamentos',
    marca: { logo: 'nubank', sigla: 'nu', fundo: '#820ad1', texto: '#ffffff' },
  },
  { procurar: 'itau', marca: { logo: 'itau', sigla: 'It', fundo: '#ec7000', texto: '#ffffff' } },
  {
    procurar: 'bradesco',
    marca: { logo: 'bradesco', sigla: 'Br', fundo: '#cc092f', texto: '#ffffff' },
  },
  {
    procurar: 'santander',
    marca: { logo: 'santander', sigla: 'St', fundo: '#ec0000', texto: '#ffffff' },
  },
  { procurar: 'inter', marca: { logo: 'inter', sigla: 'in', fundo: '#ff7a00', texto: '#ffffff' } },
  { procurar: 'c6', marca: { logo: 'c6', sigla: 'C6', fundo: '#242424', texto: '#ffffff' } },
  { procurar: 'btg', marca: { logo: 'btg', sigla: 'BT', fundo: '#051f3f', texto: '#ffffff' } },
  {
    procurar: 'mercado pago',
    marca: { logo: 'mercadopago', sigla: 'MP', fundo: '#00aae4', texto: '#ffffff' },
  },
  {
    procurar: 'picpay',
    marca: { logo: 'picpay', sigla: 'Pp', fundo: '#11c76f', texto: '#ffffff' },
  },
  {
    procurar: 'sicoob',
    marca: { logo: 'sicoob', sigla: 'Sb', fundo: '#003641', texto: '#ffffff' },
  },
  { procurar: 'xp', marca: { logo: 'xp', sigla: 'XP', fundo: '#000000', texto: '#ffffff' } },

  // Sem simbolo oficial aqui: o icone publicado do Banco do Brasil nao e o
  // dele, e os da Caixa e do Sicredi so existem em dezesseis pixels, que
  // ampliados viram borrao. Marca desenhada resolve melhor.
  { procurar: 'banco do brasil', marca: { sigla: 'BB', fundo: '#fae128', texto: '#0033a0' } },
  { procurar: 'caixa', marca: { sigla: 'CX', fundo: '#0070af', texto: '#ffffff' } },
  { procurar: 'sicredi', marca: { sigla: 'Sc', fundo: '#3fa110', texto: '#ffffff' } },

  { procurar: 'will', marca: { sigla: 'wi', fundo: '#ffd100', texto: '#111111' } },
  { procurar: 'pagbank', marca: { sigla: 'Pg', fundo: '#0fbb61', texto: '#ffffff' } },
  { procurar: 'pagseguro', marca: { sigla: 'Pg', fundo: '#0fbb61', texto: '#ffffff' } },
  { procurar: 'neon', marca: { sigla: 'Ne', fundo: '#00a5ff', texto: '#ffffff' } },
  { procurar: 'original', marca: { sigla: 'Or', fundo: '#00854a', texto: '#ffffff' } },
  { procurar: 'safra', marca: { sigla: 'Sf', fundo: '#003e7e', texto: '#ffffff' } },
  { procurar: 'banrisul', marca: { sigla: 'Bn', fundo: '#0066b3', texto: '#ffffff' } },
  { procurar: 'brb', marca: { sigla: 'BR', fundo: '#005ca9', texto: '#ffffff' } },
];

/** Banco que nao esta na lista: inicial do nome, no cinza do sistema. */
const GENERICA: Marca = { sigla: '', fundo: 'var(--color-surface-2)', texto: 'var(--color-text)' };

export function MarcaDoBanco({ nome, tamanho = 32 }: { nome: string | null; tamanho?: number }) {
  const marca = marcaDe(nome);

  if (marca.logo) {
    return (
      <img
        className="marca-banco"
        src={`/bancos/${marca.logo}.png`}
        alt=""
        aria-hidden="true"
        width={tamanho}
        height={tamanho}
        loading="lazy"
      />
    );
  }

  return (
    <span
      className="marca-banco"
      aria-hidden="true"
      style={{
        width: tamanho,
        height: tamanho,
        background: marca.fundo,
        color: marca.texto,
        fontSize: Math.round(tamanho * 0.4),
      }}
    >
      {marca.sigla}
    </span>
  );
}

function marcaDe(nome: string | null): Marca {
  const limpo = (nome ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

  for (const item of MARCAS) {
    if (limpo.includes(item.procurar)) return item.marca;
  }

  return { ...GENERICA, sigla: (limpo[0] ?? '?').toUpperCase() };
}
